import { useState, useRef, useCallback, useEffect } from "react";
import { webSocketService } from "../services/WebSocketService";
import {
  HeaderType,
  Message,
  MessageSender,
  MessageStatus,
  MessageType,
  SocketConnectionStatus,
} from "../store/types";
import { useMessageStore } from "../store";
import { recall } from "../api/message";
import { tryParse } from "../utils/object";
import { parseStringToDom } from "../utils/dom";
import { copyToClipboard } from "../utils/string";
import { Toast } from "@douyinfe/semi-ui";
import Locale from "@/app/locales";
import { getFileType } from "../utils/file";
import { ReportScene, reportScene } from "../services/DataReporter";

interface MessageActions {
  showMenu: boolean;
  messageRef: React.RefObject<HTMLDivElement>;
  handleContextMenu: (e: React.MouseEvent) => void;
  handleTouchStart: () => void;
  handleTouchEnd: () => void;
  handleWithdraw: () => void;
  closeMenu: () => void;
  handleQuoted: () => void;
  loading: boolean;
  canWithdraw: () => boolean | null;
  handleCopy: (msg: Message) => void;
}

export const useMessageActions = (
  message: Message,
  isOwnMessage: boolean,
  menuRef: React.RefObject<HTMLDivElement | null>,
): MessageActions => {
  const [showMenu, setShowMenu] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const { chatStatus, isConnected, setQuotedMsg } = useMessageStore();
  const [loading, setLoading] = useState(false);

  /**
   * 检查是否可撤回（3分钟内）
   * 1. 用户消息，并且发送成功的消息
   * 2. 结束会话不能撤回
   */
  const canWithdraw = () =>
    message.senderType === MessageSender.USER &&
    MessageStatus.Success === message.status &&
    useMessageStore.getState().chatStatus &&
    useMessageStore.getState().isConnected ===
      SocketConnectionStatus.CONNECTED &&
    !message.disableWithdraw &&
    Date.now() - message.timestamp < 3 * 60 * 1000;

  // 处理右键菜单
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const { isConnected } = useMessageStore.getState();
      if (
        isConnected === SocketConnectionStatus.CONNECTED &&
        message.senderType !== MessageSender.SYSTEM &&
        message.headerType !== HeaderType.SYSTEM
      ) {
        setShowMenu(true);
      }
    },
    [isConnected, message.senderType, message.headerType],
  );

  // 处理长按事件（移动端）
  const handleTouchStart = useCallback(() => {
    if (
      useMessageStore.getState().isConnected ===
        SocketConnectionStatus.CONNECTED &&
      message.senderType !== MessageSender.SYSTEM &&
      message.headerType !== HeaderType.SYSTEM
    ) {
      longPressTimer.current = setTimeout(() => {
        setShowMenu(true);
      }, 500); // 500ms长按触发
    }
  }, [isConnected, message.senderType, message.headerType]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleCopy = useCallback(async (msg: Message) => {
    let contentText = "";
    if (msg.type === MessageType.Text) {
      // 用户文本消息直接使用text
      contentText = tryParse(msg.content)?.text || "";
      if (msg.senderType !== MessageSender.USER) {
        // 只有非用户的消息才需要转换
        const dom = parseStringToDom(contentText);
        contentText = dom.innerText.replace(/\n+/g, " ").trim();
      }
    } else {
      const fileContent = tryParse(msg.content);
      if (fileContent) {
        // 如果是图片，视频，音频类型，则复制文件链接
        if (
          [
            MessageType.Video,
            MessageType.Image,
            MessageType.Audio,
            MessageType.File,
          ].includes(getFileType(fileContent.fileName))
        ) {
          contentText = fileContent.fileUrl;
        }
      }
    }
    if (!contentText) {
      reportScene(ReportScene.COPY_ERROR, JSON.stringify(msg));
      return;
    }
    await copyToClipboard(contentText);
    Toast.success(Locale.tips.copySuccess);
  }, []);

  const handleQuoted = () => {
    setQuotedMsg(message);
  };

  // 撤回消息（用户主动撤回）
  const handleWithdraw = useCallback(async () => {
    if (isOwnMessage && canWithdraw()) {
      const msgId = message.id as string;
      if (loading) {
        return;
      }
      try {
        setLoading(true);
        await recall(msgId);
        webSocketService.withdrawMessage(msgId, MessageSender.USER);
      } finally {
        setLoading(false);
      }
      closeMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnMessage, message.id, loading]);

  // 关闭菜单
  const closeMenu = useCallback(() => setShowMenu(false), []);

  // 处理点击外部隐藏菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu, closeMenu, menuRef]);

  return {
    showMenu,
    messageRef: messageRef as React.RefObject<HTMLDivElement>,
    handleContextMenu,
    handleTouchStart,
    handleTouchEnd,
    handleWithdraw,
    handleQuoted,
    closeMenu,
    canWithdraw,
    handleCopy,
    loading,
  };
};
